import jsPDF from 'jspdf';
import type { DecisionDetail } from '@hector/shared-types';
import { makeProseCursor, renderProseBody, PROSE_MARGIN_MM } from './prosePdf';
import { HECTOR_LOGO_PNG_DATA_URI } from './hectorLogo';
import {
  splitDecisionIntoParagraphs,
  formatDecisionTitle,
  formatDecisionCitation,
} from './decisionText';

/**
 * Rendu PDF d'une décision du corpus — moteur texte jsPDF (texte SÉLECTIONNABLE,
 * pas une capture image), même découpage en paragraphes que le viewer à l'écran
 * (`splitDecisionIntoParagraphs`) : le PDF reflète exactement ce que l'avocat lit.
 *
 * WHY une fonction PURE qui rend le document sans le sauvegarder : elle a DEUX
 * consommateurs aux sorties incompatibles — le navigateur (`output('blob')` puis
 * téléchargement) et le backend (`output('arraybuffer')` puis upload S3). Aucun
 * des deux ne doit imposer sa forme de sortie à l'autre, sinon les deux rendus
 * divergent — c'était le risque principal identifié à la conception.
 *
 * En-tête : logo Hector + plume en haut à droite, titre de juridiction avec ville,
 * citation (date longue · n° · ECLI).
 */

/** Largeur du logo en mm, haut à droite. */
const LOGO_W = 26;
/** Repli de hauteur si le PNG ne déclare pas de dimensions exploitables. */
const LOGO_FALLBACK_H = 9;

/** Un lien de retour imprimé sous la citation. */
export interface DecisionBackLink {
  label: string;
  url: string;
}

/**
 * Hôtes de `source_url` considérés comme fiables pour être imprimés dans le PDF.
 * WHY une allowlist : `source_url` vient du corpus et finit en lien cliquable dans
 * un document que l'avocat peut transmettre — on n'imprime pas une URL arbitraire.
 * Même politique que l'allowlist de `decisionReference.ts` côté backend.
 */
const TRUSTED_SOURCE_HOSTS = new Set([
  'courdecassation.fr',
  'legifrance.gouv.fr',
  'curia.europa.eu',
  'hudoc.echr.coe.int',
  'eur-lex.europa.eu',
]);

/** Hôte lisible d'une URL, sans `www.`, ou `null` si l'URL est inexploitable. */
function trustedHost(sourceUrl: string | null): string | null {
  if (!sourceUrl) return null;
  try {
    const host = new URL(sourceUrl).hostname.replace(/^www\./, '');
    return TRUSTED_SOURCE_HOSTS.has(host) ? host : null;
  } catch {
    return null;
  }
}

/**
 * Liens de retour vers la décision, du plus utile hors Hector au moins utile.
 *
 * `workspaceBaseUrl` absent ⇒ pas de lien viewer (on ne devine pas l'hôte : une URL
 * fabriquée serait un lien mort dans un document que l'avocat peut transmettre).
 */
export function decisionBackLinks(
  decision: DecisionDetail,
  workspaceBaseUrl?: string,
): DecisionBackLink[] {
  const links: DecisionBackLink[] = [];

  // Source officielle : judiciaire uniquement — `source_url` est vide sur ~100 %
  // des décisions administratives, le viewer interne EST alors la seule source.
  const host = decision.ordre === 'judiciaire' ? trustedHost(decision.sourceUrl) : null;
  if (host && decision.sourceUrl) {
    links.push({ label: `Consulter la décision sur ${host}`, url: decision.sourceUrl });
  }

  if (workspaceBaseUrl) {
    const base = workspaceBaseUrl.replace(/\/+$/, '');
    links.push({
      label: 'Ouvrir la décision dans Hector',
      url: `${base}/decisions/${decision.ordre}/${decision.id}`,
    });
  }

  return links;
}

/**
 * Rend la décision et retourne le document jsPDF, sans effet de bord.
 * Synchrone : le logo est un data URI embarqué (cf. `hectorLogo.ts`), il n'y a
 * plus de chargement d'image à attendre.
 *
 * @param workspaceBaseUrl - origine du workspace (`window.location.origin` côté
 *   navigateur). Omise, le lien « Ouvrir dans Hector » n'est pas imprimé — on ne
 *   fabrique jamais une URL devinée dans un document transmissible.
 */
export function renderDecisionPdf(decision: DecisionDetail, workspaceBaseUrl?: string): jsPDF {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const title = formatDecisionTitle(decision);

  // Logo Hector + plume, haut à droite (branding du document téléchargé).
  let logoBottom = PROSE_MARGIN_MM;
  try {
    // `getImageProperties` lit l'en-tête PNG du data URI : ratio exact sans objet Image.
    const props = pdf.getImageProperties(HECTOR_LOGO_PNG_DATA_URI);
    const logoH = props.width > 0 ? LOGO_W * (props.height / props.width) : LOGO_FALLBACK_H;
    // `alias` + `compression` : SANS eux, jsPDF embarque le PNG en flux brut et un
    // PDF de 3 paragraphes pèse 3,1 Mo (mesuré le 2026-09-10) — pour un logo de 35 Ko.
    // Ces fichiers étant désormais STOCKÉS sur S3 comme pièces du dossier, le poids
    // n'est plus seulement une gêne au téléchargement. L'alias déduplique le flux si
    // le logo est posé plusieurs fois (documents multi-pages à venir).
    pdf.addImage(
      HECTOR_LOGO_PNG_DATA_URI,
      'PNG',
      pageWidth - PROSE_MARGIN_MM - LOGO_W,
      PROSE_MARGIN_MM,
      LOGO_W,
      logoH,
      'hectorLogo',
      'MEDIUM',
    );
    logoBottom = PROSE_MARGIN_MM + logoH;
  } catch {
    // Logo optionnel : on poursuit sans bloquer la génération.
  }

  // Titre (serif, gras) — borné à gauche du logo pour ne pas le chevaucher.
  pdf.setFont('times', 'bold');
  pdf.setFontSize(15);
  pdf.setTextColor(38, 38, 38);
  const titleLines = pdf.splitTextToSize(title, pageWidth - 2 * PROSE_MARGIN_MM - LOGO_W - 4) as string[];
  let y = PROSE_MARGIN_MM + 5;
  pdf.text(titleLines, PROSE_MARGIN_MM, y);
  y += titleLines.length * 6.5;

  // Citation : date longue · n° · ECLI.
  const citation = [formatDecisionCitation(decision), decision.ecli].filter(Boolean).join(' · ');
  if (citation) {
    pdf.setFont('times', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(110, 110, 110);
    pdf.text(citation, PROSE_MARGIN_MM, y);
    y += 6;
  }

  // Liens de retour vers la décision — LA raison d'être de ce PDF quand il est
  // enregistré dans un dossier. WHY dans le document et non dans l'interface : le
  // geste naturel de l'avocat sur une pièce est le double-clic, qui ouvre le PDF ;
  // une action rangée dans un menu contextuel ne serait presque jamais découverte.
  // Porté par le fichier, le lien survit aussi au téléchargement et au transfert.
  //
  // Deux liens, dans cet ordre d'utilité décroissante hors Hector :
  //  1. la source officielle (judiciaire uniquement — 0 % des décisions
  //     administratives portent un `source_url`), publique, sans authentification ;
  //  2. le viewer Hector, qui rouvre la décision avec sa chaîne procédurale.
  y += 1;
  for (const link of decisionBackLinks(decision, workspaceBaseUrl)) {
    pdf.setFont('times', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(37, 99, 235); // bleu lien
    pdf.textWithLink(link.label, PROSE_MARGIN_MM, y, { url: link.url });
    y += 4.5;
  }

  // Corps : paragraphes structurés (même découpage que le viewer), via le moteur prose.
  pdf.setTextColor(0, 0, 0);
  const bodyStartY = Math.max(y + 3, logoBottom + 4);
  const cursor = makeProseCursor(pdf, bodyStartY);
  const body = splitDecisionIntoParagraphs(decision.fullText).join('\n\n');
  renderProseBody(cursor, body || 'Texte intégral indisponible pour cette décision.');

  return pdf;
}

/** Slugifie un libellé : minuscules, sans accents, alphanum + underscores. */
function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

/**
 * Libellé de la PIÈCE quand une décision est enregistrée dans un dossier —
 * lisible par l'avocat ET porteur des identifiants.
 *
 * WHY ici et pas de chaque côté : `find_case_documents` fait un `ILIKE` sur `name`
 * et `piece_description`, les deux seules colonnes par lesquelles l'agent retrouve
 * la décision tant que la vectorisation n'est pas finie. Deux implémentations
 * dériveraient, et la moitié des pièces cesserait d'être trouvable selon qu'elles
 * ont été enregistrées par l'avocat ou par l'agent.
 *
 * Les `/` des numéros de RG (« 24/01072 ») sont remplacés : le libellé sert aussi
 * de nom de fichier affiché.
 */
export function decisionPieceName(decision: DecisionDetail): string {
  const numero = decision.numero ? ` - n° ${decision.numero}` : '';
  return `${formatDecisionTitle(decision)}${numero}.pdf`.replace(/[/\\]/g, '-');
}

/** Description de la pièce : la ligne de citation, ECLI compris. */
export function decisionPieceDescription(decision: DecisionDetail): string {
  return [formatDecisionCitation(decision), decision.ecli].filter(Boolean).join(' · ');
}

/**
 * Nom de fichier canonique d'une décision : `Decision_<titre>_<numéro>_<date>.pdf`.
 * Partagé par le téléchargement navigateur et la pièce créée côté serveur, pour
 * que l'avocat retrouve le même nom dans les deux cas.
 */
export function decisionPdfFileName(decision: DecisionDetail): string {
  const title = formatDecisionTitle(decision);
  const numeroPart = decision.numero ? `${slugify(decision.numero)}_` : '';
  const today = new Date().toISOString().slice(0, 10);
  return `Decision_${slugify(title)}_${numeroPart}${today}.pdf`;
}
